<template>
  <div id="app" :class="{ home: this.$route.name == 'home' }">
    <header>
      <router-link :to="{ name: 'home' }" class="so-logo no-select">
        SO
      </router-link>
      <navigation></navigation>
    </header>
    <transition name="roll" mode="out-in" @before-enter="afterEnter" appear>
      <router-view></router-view>
    </transition>
    <so-footer v-if="$route.name != 'project'"></so-footer>
  </div>
</template>

<script>
const Navigation = () =>
  import(/* webpackChunkName: "Navigation" */ "@/components/Navigation");
const SoFooter = () =>
  import(/* webpackChunkName: "Footer" */ "@/components/SoFooter");

export default {
  components: {
    Navigation,
    SoFooter
  },
  metaInfo() {
    return {
      titleTemplate: "SO Represent" + " %s"
    };
  },
  data() {
    return {
      isLoaded: false
    };
  },
  beforeCreate() {
    this.$store.dispatch("loadSite");
  },
  computed: {
    site() {
      return this.$store.state.site;
    }
  },
  methods: {
    afterEnter() {
      this.$root.$emit("scrollAfterEnter");
    }
  }
};
</script>

<style lang="scss">
@import "node_modules/reset-css/sass/_reset";
@font-face {
  font-family: "SORepresent-Light";
  src: url("/public/font/SORepresent-Light.woff") format("woff");
  font-style: normal;
  font-weight: 400;
  font-display: swap;
}
@font-face {
  font-family: "Kolektiv-Light";
  src: url("/public/font/Kolektiv-Light.woff") format("woff"),
    url("/public/font/Kolektiv-Light.woff") format("woff2"),
    url("/public/font/Kolektiv-Light.woff") format("eot");
  font-style: normal;
  font-weight: 400;
  font-display: swap;
}
@font-face {
  font-family: "Kolektiv-LightItalic";
  src: url("/public/font/Kolektiv-LightItalic.woff") format("woff"),
    url("/public/font/Kolektiv-LightItalic.woff") format("woff2"),
    url("/public/font/Kolektiv-LightItalic.woff") format("eot");
  font-style: normal;
  font-weight: 400;
  font-display: swap;
}
* {
  box-sizing: border-box;
}

.no-select {
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.roll-enter-active,
.roll-leave-active {
  transition: all 1s;
}
.roll-enter {
  .roller {
    height: calc(100vh + 200px) !important;
    top: auto !important;
    bottom: -200px !important;
  }
  opacity: 0.99;
}
.roll-enter-to {
  .roller {
    height: 0 !important;
    top: auto !important;
    bottom: -200px !important;
  }
  opacity: 0.99;
}
.roll-leave {
  .roller {
    height: 0 !important;
  }
  opacity: 0.99;
}
.roll-leave-to {
  .roller {
    height: calc(100vh + 200px) !important;
  }
  opacity: 0.99;
}

.link,
a,
a:active,
a:hover,
a:visited {
  color: black;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.5s ease;
  &:hover {
    color: rgba(0, 0, 0, 0.3);
  }
}

img {
  display: block;
}
img[lazy="loaded"] {
  opacity: 1;
  transition: opacity 1s 0.5s;
}
img[lazy="loading"] {
  opacity: 0 !important;
}

.italic {
  font-family: "Kolektiv-LightItalic", Times, serif;
}

.loading {
  animation: blink 1.5s ease infinite alternate;
}
@keyframes blink {
  0% {
    opacity: 0;
  }
  20% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

html {
  width: 100%;
  height: 100%;
}

body {
  width: 100%;
  height: auto;
  &.home {
    height: 100%;
  }
  background: rgb(250,250,250);
  overflow-x: hidden;
  scroll-behavior: smooth;
  text-rendering: optimizeSpeed;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  --fs: 1rem;
  --lh: calc(1.2 * 1rem);
  font-size: var(--fs);
  line-height: var(--lh);
}

#app {
  width: 100%;
  height: auto;
  font-family: "Kolektiv-Light", Times, serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-size: var(--fs);
  line-height: var(--lh);
  &.home {
    height: 100%;
  }
  header {
    position: fixed;
    z-index: 999;
    top: 0;
    left: 0;
    width: 100%;
    pointer-events: none;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    .so-logo {
      pointer-events: auto;
      padding: 1.3rem 1.5rem 1.5rem;
      font-family: "SORepresent-Light", sans-serif;
      font-feature-settings: "ss01";
      font-size: calc(4 * var(--fs));
      line-height: 1;
      @media only screen and (max-width: 768px) {
        font-size: calc(1.9 * var(--fs));
      }
    }
  }
  main {
    min-height: calc(100% - (4 * var(--lh)));
    .roller {
      position: fixed;
      top: -200px;
      left: 0;
      width: 100%;
      height: 0;
      background: rgb(250,250,250);
      box-shadow: 0 0 100px 100px rgb(250,250,250);
      transform: translate3d(0, 0, 0);
      z-index: 999;
      transition: height 1s;
    }
    section {
      .title {
        white-space: nowrap;
        flex: 0 0 auto;
        max-width: calc(100% - (10 * var(--lh)));
        height: auto;
        margin-left: calc(6 * var(--lh));
        padding: 1.3rem 0 0 1.5rem;
        font-family: "SORepresent-Light", Times, serif;
        font-size: calc(4 * var(--fs));
        text-transform: uppercase;
        letter-spacing: 0.025em;
        line-height: 1;
        position: relative;
        z-index: 1;
        display: inline-block;
        position: sticky;
        top: 0;
        transition: color .5s ease;
        &:has(.produce:hover) a, &:has(.produce:hover) {
                color: rgba(0, 0, 0, 0.3);
            }
        &.fixed {
            &.show {
                opacity: 1;
            }
            position:fixed;
            left:0;
            opacity:0;
            transition: color .5s ease, opacity .5s ease;
            &.produce, &.both, &.represent {
                opacity:1 !important;
            }

        }
        &.produce, &.produce a {
            color: rgba(0, 0, 0, 0.3);
        }
        &.represent {
            color: black;
            a:not(.represent) {
                color: black;
            }
            span.represent a {
                color: rgba(0, 0, 0, 0.3);
            }
        }
        &.both {
            color:black;
        }
        &.about {
          white-space:initial;
        }
        &.kolektiv {
          padding: 1.1rem 0 0 1.5rem;
          letter-spacing: 0;
          font-family: "Kolektiv-LightItalic", serif !important;
          text-transform: none !important;
          font-size: calc(4.1 * var(--fs));
        }
        p {
          margin-bottom: 0;
        }
        &.artists {
          padding-top: 0;
          line-height: 1;
          a {
            display: inline-block;
          }
          div {
            width: 100%;
          }
        }
        &.about p {
          margin-bottom: calc(3 * var(--lh));
          &:last-of-type {
            margin-bottom: 0;
          }
        }
        &.flex {
          width: calc(100% - (10 * var(--lh)));
          display: flex;
          flex-wrap: wrap;
          padding: 1.3rem 0 0 1.5rem;
          span {
            max-width: 100%;
            flex: 0 0 auto;
            padding-right: 1.5rem;
            padding-bottom: 1.5rem;
          }
          div {
            flex: 1 0 auto;
            &.balign {
              display: flex;
              padding-bottom: 1.8rem;
              align-items: flex-end !important;
              @media only screen and (max-width: 768px) {
                padding-bottom: 1.5rem;
              }
            }
          }
        }
        @media only screen and (max-width: 1024px) {
          white-space: initial;
          padding: 1.3rem 1.5rem 1.5rem;
          flex: 0 0 calc(100% - (3.8 * var(--fs)));
        }
        @media only screen and (max-width: 768px) {
          max-width: calc(100% - (2.75 * var(--lh)));
          margin-left: calc(2.75 * var(--lh));
          padding: 1.3rem 1.5rem 1.5rem;
          font-size: calc(1.9 * var(--fs));
          &.flex {
            width: calc(100% - (6 * var(--lh)));
          }
        }
      }
    }
  }
  li,
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
}
</style>
